import { v4 } from "uuid";
import { LogEntry, User, WebinarRB, WebinarSessionParticipant } from "../../../models";

export class WebinarDB {
    public id: string;
    public name: string;
    public subject: string;
    public userId: string;
    public webinarRB: WebinarRB;
    public sessions?: WebinarSessionDB[];
    public active: boolean = false;
    public displayParams: { label: string; value: string; }[] = [];

    public static fromPOJO(data: any): WebinarDB { return Object.assign(new WebinarDB(), data); }
    public static createFromWebinarRB(webinarRB: WebinarRB): WebinarDB {
        const webinarDB = new WebinarDB();
        webinarDB.id = webinarRB.id;
        webinarDB.name = webinarRB.name;
        webinarDB.subject = webinarRB.subject;
        webinarDB.webinarRB = webinarRB;
        webinarDB.displayParams.push({ label: 'name', value: webinarRB.name });
        webinarDB.displayParams.push({ label: 'id', value: webinarRB.id });
        return webinarDB;
    }
}

export class WebinarSessionDB {
    public id: string;
    public webinarId: string;
    public joinDate!: number;
    public leaveDate?: number;
    public logs?: WebinarLogEntryDB[];

    public static fromPOJO(data: any): WebinarSessionDB { return Object.assign(new WebinarSessionDB(), data); }
    public static create(webinarDB: WebinarDB): WebinarSessionDB {
        const webinarSessionDB = new WebinarSessionDB();
        webinarSessionDB.id = v4();
        webinarSessionDB.webinarId = webinarDB.id;
        return webinarSessionDB;
    }
}

export class WebinarUserDB {
    public id: string;
    public displayName: string;
    public avatarURL: string;
    public companyName: string;
    public displayParams: { label: string; value: string; }[] = [];

    public static fromPOJO(data: any): WebinarUserDB { return Object.assign(new WebinarUserDB(), data); }
    public static createFromUserRB(userRB: User): WebinarUserDB {
        const userDB = new WebinarUserDB();
        userDB.id = userRB.dbId;
        userDB.displayName = userRB.displayName;
        userDB.avatarURL = userRB.avatar.src;
        userDB.companyName = userRB.companyInfo.name;
        userDB.displayParams.push({ label: 'id', value: userRB.dbId });
        return userDB;
    }
}

export class WebinarLogEntryUserStateDB {
    public mutedSFU: boolean = true;
    public mutedMCU: boolean = true;
    public stageAudioLevel: number = -1;
    public audioConfig: string = 'backstage';
    public stage: any[] = [];
    public get muted(): boolean { return this.mutedMCU && this.mutedSFU; }
}

export class WebinarLogEntryDB {
    public id?: number;
    public sid?: string;
    public timestamp: number;
    public webinarId: string;
    public sessionId: string;
    public level: string;
    public type: 'action' | 'event' = 'action';
    public filter?: string;
    public method: string;
    public info?: string;
    public analysis?: string;
    public requestId?: string;
    public userId?: string;
    public askById?: string;
    public correlatorId?: string;
    public rawMethod?: string;
    public rawData?: string;
    public rawDataType?: string;
    public userState: WebinarLogEntryUserStateDB = new WebinarLogEntryUserStateDB();

    public static fromPOJO(data: any): WebinarLogEntryDB { return Object.assign(new WebinarLogEntryDB(), data); }

    public static createFromLogEntry(webinarDB: WebinarDB, sessionDB: WebinarSessionDB, logEntry: LogEntry): WebinarLogEntryDB {
        const webinarLogEntryDB = new WebinarLogEntryDB();
        webinarLogEntryDB.webinarId = webinarDB.id;
        webinarLogEntryDB.sessionId = sessionDB.id;
        webinarLogEntryDB.userId = logEntry.params['part'];
        webinarLogEntryDB.timestamp = logEntry.date.getTime();
        webinarLogEntryDB.level = logEntry.level;
        webinarLogEntryDB.method = logEntry.method;
        webinarLogEntryDB.rawMethod = logEntry.rawMethod;
        webinarLogEntryDB.rawDataType = logEntry.rawDataType;
        webinarLogEntryDB.rawData = logEntry.rawData;
        webinarLogEntryDB.correlatorId = logEntry.params['correlatorId'];
        if (logEntry.params['askBy']) webinarLogEntryDB.askById = logEntry.params['askBy'];
        return webinarLogEntryDB;
    }

    public updateUserState(webinarDB: WebinarDB): void {
        const webinar = webinarDB?.webinarRB;
        if (webinar) {
            const localParticipant = webinar.session?.localParticipant;
            if (localParticipant) {
                this.userState.mutedMCU = localParticipant.muteMCU;
                this.userState.mutedSFU = localParticipant.muteSFU;
                if (localParticipant.audioConfig) this.userState.audioConfig = localParticipant.audioConfig;
                this.userState.stage = webinar.session.speakerParticipants.concat([localParticipant]).reduce((acc: any[], part: WebinarSessionParticipant) => {
                    if (!part?.id) return acc;
                    if (part.onStage || part.sharingOnStage) {
                        acc.push({ id: part.id, name: part.id, audioVideo: part.onStage, sharing: part.sharingOnStage });
                    }
                    return acc;
                }, []);
            }
        }
    }

    public setMajor(): void { if (this.level !== 'error') this.level = 'major'; }
}
